import { Helmet } from "react-helmet-async";
import { useState, useEffect } from "react";
import api from "../Utils/Axios";

function Recognitions() {
  const [companyLanguage, setCompanyLanguage] = useState('en');
  const [recognitions, setRecognitions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Get company language from localStorage
    const lang = localStorage.getItem("companyLanguage") || 'en';
    setCompanyLanguage(lang);
    
    // Listen for language updates
    const handleStorageChange = () => {
      const updatedLang = localStorage.getItem("companyLanguage") || 'en';
      setCompanyLanguage(updatedLang);
    };
    
    window.addEventListener('companyDataUpdated', handleStorageChange);
    
    // Check periodically
    const interval = setInterval(() => {
      const updatedLang = localStorage.getItem("companyLanguage") || 'en';
      if (updatedLang !== companyLanguage) {
        setCompanyLanguage(updatedLang);
      }
    }, 2000);

    return () => {
      window.removeEventListener('companyDataUpdated', handleStorageChange);
      clearInterval(interval);
    };
  }, [companyLanguage]);

  useEffect(() => {
    fetchRecognitions();
  }, []);

  const fetchRecognitions = async () => {
    try {
      setIsLoading(true);
      const response = await api.get("/admin/recognitions");
      setRecognitions(response.data.recognitions || []);
    } catch (error) {
      console.error("Fetch recognitions error:", error);
      setRecognitions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const isUrdu = companyLanguage === 'ur';

  // Fallback dummy recognitions if API fails
  const dummyRecognitions = [
    {
      id: 1,
      title: "Best Business Solution 2024",
      titleUrdu: "بہترین کاروباری حل 2024",
      description: "Awarded for excellence in providing innovative business solutions for commission agents.",
      descriptionUrdu: "کمیشن ایجنٹس کے لیے جدید کاروباری حل فراہم کرنے میں بہترین کارکردگی کے لیے ایوارڈ",
      year: "2024",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=300&fit=crop",
      category: "Business Excellence"
    },
    {
      id: 2,
      title: "Digital Innovation Award",
      titleUrdu: "ڈیجیٹل انوویشن ایوارڈ",
      description: "Recognized for outstanding contribution to digital transformation in traditional markets.",
      descriptionUrdu: "روایتی مارکیٹوں میں ڈیجیٹل تبدیلی میں نمایاں تعاون کے لیے تسلیم شدہ",
      year: "2023",
      image: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=400&h=300&fit=crop",
      category: "Innovation"
    },
    {
      id: 3,
      title: "Customer Satisfaction Excellence",
      titleUrdu: "کسٹمر کی اطمینان کی بہترین کارکردگی",
      description: "Achieved highest customer satisfaction ratings in the industry for three consecutive years.",
      descriptionUrdu: "تین سال مسلسل صنعت میں سب سے زیادہ کسٹمر اطمینان کی درجہ بندی حاصل کی",
      year: "2023",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=300&fit=crop",
      category: "Customer Service"
    },
    {
      id: 4,
      title: "Technology Leadership",
      titleUrdu: "ٹیکنالوجی کی قیادت",
      description: "Leading the way in technology adoption for traditional business sectors.",
      descriptionUrdu: "روایتی کاروباری شعبوں میں ٹیکنالوجی کے استعمال میں قیادت",
      year: "2022",
      image: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=400&h=300&fit=crop",
      category: "Technology"
    },
    {
      id: 5,
      title: "Regional Business Impact",
      titleUrdu: "علاقائی کاروباری اثر",
      description: "Recognized for significant positive impact on local business communities.",
      descriptionUrdu: "مقامی کاروباری کمیونٹیز پر نمایاں مثبت اثر کے لیے تسلیم شدہ",
      year: "2022",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=300&fit=crop",
      category: "Community Impact"
    },
    {
      id: 6,
      title: "Sustainable Business Practices",
      titleUrdu: "پائیدار کاروباری طریقے",
      description: "Awarded for commitment to sustainable and ethical business practices.",
      descriptionUrdu: "پائیدار اور اخلاقی کاروباری طریقوں کے عزم کے لیے ایوارڈ",
      year: "2021",
      image: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=400&h=300&fit=crop",
      category: "Sustainability"
    }
  ];

  return (
    <>
      <Helmet>
        <title>{companyLanguage === 'ur' ? 'تعریفیں - Galla Mandi Bills & Khata' : 'Recognitions - Galla Mandi Bills & Khata'}</title>
      </Helmet>
      
      <div
        className={`${isUrdu ? 'font-urdu' : ''} bg-brand-background min-h-screen`}
        style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}
        dir={isUrdu ? 'rtl' : 'ltr'}
      >
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-brand-header to-green-700 text-white py-20">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-5xl font-bold mb-4">
              {companyLanguage === 'ur' ? 'تعریفیں اور ایوارڈز' : 'Recognitions & Awards'}
            </h1>
            <p className="text-xl opacity-90">
              {companyLanguage === 'ur' 
                ? 'ہماری کامیابیوں اور تعریفوں کا جشن منائیں' 
                : 'Celebrating our achievements and recognitions'}
            </p>
          </div>
        </section>

        {/* Recognitions Section */}
        <section className={`py-20 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
          <div className="container mx-auto px-4">
            {isLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-header mx-auto"></div>
                <p className="mt-4 text-gray-600">
                  {companyLanguage === 'ur' ? 'تعریفیں لوڈ ہو رہی ہیں...' : 'Loading recognitions...'}
                </p>
              </div>
            ) : recognitions.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600">
                  {companyLanguage === 'ur' ? 'کوئی تعریف نہیں ملی' : 'No recognitions found'}
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {recognitions.map((recognition) => (
                  <div
                    key={recognition._id}
                    className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
                  >
                    <div className="relative h-48 overflow-hidden">
                      {recognition.image ? (
                        <img
                          src={recognition.image}
                          alt={recognition.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-brand-header to-green-600 flex items-center justify-center">
                          <span className="text-white text-4xl font-bold">
                            {recognition.title.charAt(0)}
                          </span>
                        </div>
                      )}
                      <div className="absolute top-4 right-4 bg-brand-header text-white px-3 py-1 rounded-full text-sm font-semibold">
                        {recognition.year}
                      </div>
                    </div>
                    <div className="p-6">
                      {recognition.category && (
                        <div className="mb-2">
                          <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                            {isUrdu && recognition.categoryUrdu ? recognition.categoryUrdu : recognition.category}
                          </span>
                        </div>
                      )}
                      <h3 className="text-xl font-bold text-gray-900 mb-3">
                        {isUrdu && recognition.titleUrdu ? recognition.titleUrdu : recognition.title}
                      </h3>
                      {recognition.description && (
                        <p className="text-gray-600 text-sm leading-relaxed">
                          {isUrdu && recognition.descriptionUrdu ? recognition.descriptionUrdu : recognition.description}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-4xl font-bold text-brand-header mb-2">6+</div>
                <div className="text-gray-600">
                  {companyLanguage === 'ur' ? 'ایوارڈز' : 'Awards'}
                </div>
              </div>
              <div>
                <div className="text-4xl font-bold text-brand-header mb-2">3+</div>
                <div className="text-gray-600">
                  {companyLanguage === 'ur' ? 'سال کی کامیابی' : 'Years of Excellence'}
                </div>
              </div>
              <div>
                <div className="text-4xl font-bold text-brand-header mb-2">1000+</div>
                <div className="text-gray-600">
                  {companyLanguage === 'ur' ? 'مطمئن کسٹمرز' : 'Satisfied Customers'}
                </div>
              </div>
              <div>
                <div className="text-4xl font-bold text-brand-header mb-2">50+</div>
                <div className="text-gray-600">
                  {companyLanguage === 'ur' ? 'کاروباری شراکت دار' : 'Business Partners'}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default Recognitions;

